import ContentHeader from "@/components/Explore/Navi/ContentHeader";
import SideBar from "@/components/Explore/Navi/SideBar";
import TopBar from "@/components/Explore/Navi/TopBar";
import Content from "@/components/Explore/Pubmed/Content";
import { useNaviHistory, useNaviPaper, useNaviServer } from "@/hooks/useNavi";
import useTranslation from "@/hooks/useTranslation";
import CommonLayout from "@/layouts/Common";
import { naviState } from "@/stores/navi";
import { isClient } from "@/utils/common.util";
import { restoreUUID } from "@/utils/string.util";
import { NextPage, NextPageContext } from "next";
import Head from "next/head";
import React, { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import styles from "./styles.module.less";

const { container, sideBar, topBar, content } = styles;

interface IProps {
  page: number;
  size: number;
  id: string;
}

const NaviPage: NextPage<IProps> = (props) => {
  useNaviHistory();
  const { t } = useTranslation("explore");
  const { loading } = useNaviServer(props);
  const { handleFetch, handleSearch, loadingSearch } = useNaviPaper();
  const { searchParams, displayHelper, fetchResults } = useRecoilValue(
    naviState
  );
  const { count } = displayHelper;
  const { term } = searchParams;
  const { page, size } = props;
  const [localLoading, setLocalLoading] = useState(isClient);

  useEffect(() => {
    if (loading && fetchResults?.length === 0) {
      setLocalLoading(true);
    } else {
      setLocalLoading(false);
    }
  }, [fetchResults, loading]);

  const contentProps = {
    term,
    fetchResults,
    count,
    loadingFetch: loading,
    loadingSearch: loadingSearch || localLoading,
    locationInfo: { page, size },
    handleFetch,
    Header: (
      <ContentHeader pmids={fetchResults?.map((e) => e.ePmid)} term={term} />
    ),
    Footer: <div className={styles.footer}>{t("navi.content.statement")}</div>,
  };

  const topBarProps = {
    term,
    loadingSearch,
    handleSearch,
  };

  const title = useMemo(() => {
    if (term) {
      return `${term} - ${t("menu:explore.navi")} - ${t(
        "menu:explore.itself"
      )} - MedicGo`;
    }
    return `${t("menu:explore.navi")} - ${t("menu:explore.itself")} - MedicGo`;
  }, [term]);

  return (
    <CommonLayout>
      <Head>
        <title>{title}</title>
      </Head>
      <div className={container}>
        <div className={sideBar}>
          <SideBar />
        </div>
        <div className={topBar}>
          <TopBar {...topBarProps} />
        </div>
        <div className={content}>
          <Content {...contentProps} />
        </div>
      </div>
    </CommonLayout>
  );
};

NaviPage.getInitialProps = async (ctx: NextPageContext) => {
  const id = restoreUUID(ctx.query.id as string);
  const page = parseInt((ctx.query.page as string) || "1", 10);
  const size = parseInt((ctx.query.size as string) || "20", 10);
  return {
    page,
    size,
    id,
    namespacesRequired: ["explore", "common", "menu"],
  };
};

export default NaviPage;
